import React from "react";
import { useGame } from "@/contexts/GameContext";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Trophy,
  Medal,
  Award,
  Home,
  RotateCcw,
  Crown,
  CheckCircle, // Ikona dla wykonanych
  XCircle, // Ikona dla pominiętych/niewykonanych
  SkipForwardIcon, // Ikona dla użytych darmowych pominięć
} from "lucide-react";
import { motion } from "framer-motion";
import { AnimatedGroup } from "../ui/animated-group"; // Upewnij się, że ścieżka jest poprawna
import { cn } from "@/lib/utils";

const PlayerRankItem = ({ player, index }) => {
  const score = player.points || 0;
  let rankSpecificStyles = "bg-card/80 border-border hover:bg-muted/20"; // Domyślny styl karty gracza
  let rankIcon = null;
  let avatarSizeClass = "w-10 h-10 text-sm"; // Domyślny rozmiar awatara
  let nameSizeClass = "text-base"; // Domyślny rozmiar nazwy gracza

  // Style dla podium
  if (index === 0) {
    rankSpecificStyles =
      "bg-gradient-to-br from-yellow-400/20 via-amber-500/20 to-yellow-600/20 border-yellow-500/50 shadow-xl shadow-yellow-500/15 scale-[1.02] hover:scale-[1.04]";
    rankIcon = <Crown className="w-6 h-6 text-yellow-400 fill-yellow-400/30" />;
    avatarSizeClass = "w-12 h-12 text-base";
    nameSizeClass = "text-lg";
  } else if (index === 1) {
    rankSpecificStyles =
      "bg-gradient-to-br from-slate-400/20 via-gray-500/20 to-slate-600/20 border-slate-500/50 shadow-lg shadow-slate-500/15 scale-[1.01] hover:scale-[1.03]";
    rankIcon = <Medal className="w-5 h-5 text-slate-300 fill-slate-400/30" />;
    avatarSizeClass = "w-11 h-11 text-base";
    nameSizeClass = "text-md";
  } else if (index === 2) {
    rankSpecificStyles =
      "bg-gradient-to-br from-orange-500/20 via-amber-600/20 to-orange-700/20 border-orange-600/50 shadow-md shadow-orange-500/15 hover:scale-[1.02]";
    rankIcon = <Award className="w-5 h-5 text-orange-400 fill-orange-400/30" />;
  } else {
    rankIcon = (
      <span className="text-sm font-semibold text-muted-foreground">
        #{index + 1}
      </span>
    );
  }

  return (
    // Używamy motion.div jeśli AnimatedGroup go nie dostarcza, lub usuwamy jeśli tak
    <motion.div
      className={cn(
        "flex items-center gap-3 p-3 md:p-4 rounded-xl border transition-all duration-300 ease-out backdrop-blur-sm",
        rankSpecificStyles
      )}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      // transition jest obsługiwane przez AnimatedGroup, jeśli jest używane
    >
      {/* Pozycja */}
      <div className="w-8 h-8 flex items-center justify-center flex-shrink-0 text-lg">
        {rankIcon}
      </div>

      {/* Avatar */}
      <Avatar
        className={cn(avatarSizeClass, "border-2 border-border shadow-sm")}
      >
        <AvatarFallback
          className={cn(
            "font-bold bg-gradient-to-br from-pink-500 to-purple-600 text-primary-foreground"
          )}
        >
          {player.name?.charAt(0).toUpperCase() || "?"}
        </AvatarFallback>
      </Avatar>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <h3 className={cn("font-bold text-foreground truncate", nameSizeClass)}>
          {player.name}
        </h3>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground mt-1">
          <span className="flex items-center gap-1" title="Wykonane">
            <CheckCircle className="w-3.5 h-3.5 text-green-500" />
            {player.tasksCompleted || 0}
          </span>
          <span
            className="flex items-center gap-1"
            title="Pominięte/Niewykonane (z karą)"
          >
            <XCircle className="w-3.5 h-3.5 text-red-500" />
            {player.tasksSkipped || 0}
          </span>
          <span
            className="flex items-center gap-1"
            title="Użyte darmowe pominięcia"
          >
            <SkipForwardIcon className="w-3.5 h-3.5 text-yellow-500" />
            {player.skipsUsed || 0}/2
          </span>
        </div>
      </div>

      {/* Wynik */}
      <div className="text-right flex-shrink-0 pl-2">
        <div className="text-xl md:text-2xl font-bold text-foreground">
          {score}
        </div>
        <div className="text-xs text-muted-foreground uppercase">PKT</div>
      </div>
    </motion.div>
  );
};

export default function EndScreen() {
  const { state, dispatch } = useGame();

  // Sortowanie graczy według punktów (bez zmian)
  const sortedPlayers = [...state.players].sort((a, b) => {
    return (b.points || 0) - (a.points || 0);
  });

  const restartGame = () => {
    // Przy restarcie gry, chcemy wyczyścić stan gry (np. graczy, punkty)
    // i przenieść do konfiguracji nowej gry.
    // Możesz rozważyć dispatch({ type: "RESET_GAME" }) jeśli chcesz całkowity reset,
    // a następnie przejście do 'config', lub stworzyć nową akcję np. 'SETUP_NEW_GAME'
    dispatch({ type: "SET_SCREEN", payload: "config" });
    dispatch({ type: "SET_CONFIG_STEP", payload: 1 });
    // Jeśli chcesz zresetować listę graczy i ich staty przy "Nowej Grze":
    // dispatch({ type: "RESET_PLAYERS_AND_STATS" }); // Potrzebna nowa akcja
  };
  const startNewGameFromEndScreen = () => {
    dispatch({ type: "RESET_GAME" }); // Resetuje wszystko, w tym localStorage
    dispatch({ type: "SET_SCREEN", payload: "config" });
    dispatch({ type: "SET_CONFIG_STEP", payload: 1 });
  };

  const goHomeFromEndScreen = () => {
    dispatch({ type: "RESET_GAME" }); // Resetuje wszystko, w tym localStorage
    dispatch({ type: "SET_SCREEN", payload: "start" });
  };

  const goHome = () => {
    // Powrót do menu startowego - jeśli gra jest zapisywana,
    // to pozwoli ją wznowić. Jeśli nie, to po prostu wraca.
    // Jeśli "RESET_GAME" czyści localStorage, to nie będzie czego wznawiać.
    dispatch({ type: "GO_BACK_TO_START" }); // Ta akcja powinna tylko zmieniać ekran, nie resetować stanu
  };

  return (
    // Zmieniamy główny kontener na flex i pozwalamy mu rosnąć
    <main className="py-6 md:py-8 flex flex-col h-full relative items-center px-4">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-6 md:mb-8 relative z-10"
      >
        <div className="w-14 h-14 md:w-16 md:h-16 mx-auto mb-4 bg-gradient-to-br from-yellow-400 via-amber-500 to-orange-500 rounded-2xl flex items-center justify-center shadow-xl border-2 border-yellow-300/50">
          <Trophy className="w-7 h-7 md:w-8 md:h-8 text-white" />
        </div>
        <h1 className="text-4xl md:text-5xl font-black text-foreground mb-1">
          Koniec Gry!
        </h1>
        <p className="text-base text-muted-foreground">
          Oto ostateczny ranking:
        </p>
      </motion.div>

      {/* Kontener na listę graczy z przewijaniem i ograniczoną szerokością */}
      <div className="flex-1 w-full overflow-y-auto mb-6 md:mb-8 px-1 custom-scrollbar">
        <AnimatedGroup className="grid gap-2">
          {/* AnimatedGroup powinien zarządzać animacją każdego dziecka */}
          {sortedPlayers.map((player, index) => (
            <PlayerRankItem key={player.id} player={player} index={index} />
          ))}
          {/* Dodaj fallback jeśli nie ma graczy, chociaż to ekran końca gry */}
          {sortedPlayers.length === 0 && (
            <p className="text-center text-muted-foreground py-10">
              Brak graczy do wyświetlenia.
            </p>
          )}
        </AnimatedGroup>
      </div>

      {/* Przyciski akcji na dole */}
      <div className="grid sm:grid-cols-2 gap-4 w-full ">
        <Button
          onClick={startNewGameFromEndScreen}
          size="lg"
          className="font-semibold"
        >
          <RotateCcw className="size-5 mr-2" />
          Nowa Gra
        </Button>
        <Button
          onClick={goHomeFromEndScreen}
          size="lg"
          variant="outline"
          className="font-semibold"
        >
          <Home className="size-5 mr-2" />
          Menu Główne
        </Button>
      </div>
    </main>
  );
}
